import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

import { env } from '@/shared/config/env'

import { normalizeError } from './apiError'
import {
  canRefreshSession,
  getCurrentToken,
  notifySessionExpired,
  refreshSession,
} from './authBridge'

let refreshInFlight: Promise<void> | null = null
let sessionExpiryNotified = false

const notifySessionExpiredOnce = (): void => {
  if (sessionExpiryNotified) return
  sessionExpiryNotified = true
  notifySessionExpired()
}

const refreshOnce = async (): Promise<void> => {
  if (!refreshInFlight) {
    refreshInFlight = refreshSession().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

/**
 * =============================================================================
 * CLIENTE HTTP
 * =============================================================================
 * Punto ÚNICO de salida hacia el backend. Ningún componente ni hook debe usar
 * `fetch` o `axios` por su cuenta.
 *
 * ¿Por qué centralizarlo? Porque hay cosas que deben pasar en TODAS las
 * peticiones: adjuntar el token, manejar el 401, normalizar errores, poner
 * un timeout. Si cada archivo hiciera su propia llamada, cada uno olvidaría
 * algo distinto — y arreglarlo significaría editar decenas de archivos.
 *
 * Nota sobre el nombre: "HTTP" aquí es la familia del protocolo, no el
 * esquema `http://`. HTTPS es el mismo HTTP dentro de un túnel cifrado; el
 * cliente es idéntico en ambos casos. Que la conexión vaya cifrada lo decide
 * únicamente la URL (ver VITE_API_URL en shared/config/env.ts).
 * =============================================================================
 */
console.log(env.VITE_API_URL)
export const httpClient: AxiosInstance = axios.create({
  baseURL: env.VITE_API_URL,

  // Si el servidor no responde en 30 segundos, se corta. Sin timeout, una
  // petición colgada deja un spinner girando para siempre.
  timeout: 30_000,

  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },

  /**
   * withCredentials envía cookies al backend.
   *
   * 👉 PENDIENTE: ponerlo en `true` SI el backend maneja la sesión con cookies
   *    httpOnly (la opción más segura frente a XSS, ver docs/04-permisos.md).
   *    Requiere además que el backend permita credenciales en su configuración
   *    de CORS y devuelva el dominio exacto del frontend, no `*`.
   */
  withCredentials: false,
})

/* -----------------------------------------------------------------------------
 * INTERCEPTOR DE PETICIÓN — se ejecuta antes de que cada petición salga.
 * -------------------------------------------------------------------------- */
httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getCurrentToken()

  if (token) {
    // Bearer es el esquema estándar para tokens (RFC 6750).
    // 👉 PENDIENTE: confirmar con el backend que espera este formato.
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

/* -----------------------------------------------------------------------------
 * INTERCEPTOR DE RESPUESTA — se ejecuta cuando llega la respuesta.
 * -------------------------------------------------------------------------- */
httpClient.interceptors.response.use(
  // Camino feliz: se deja pasar la respuesta tal cual.
  (response) => response,

  // Camino de error: se normaliza y se reacciona a los casos globales.
  async (error: unknown) => {
    const apiError = normalizeError(error)

    // Un 401 significa que el token no sirve. Es un caso global: no tiene
    // sentido que cada pantalla lo maneje por separado. Se avisa a la feature
    // de auth, que se encarga de cerrar la sesión y redirigir al login.
    const config = (error as { config?: InternalAxiosRequestConfig & { __authRetry?: boolean } })
      .config
    const isAuthEndpoint =
      typeof config?.url === 'string' && /^\/auth\/(login|refresh|logout)$/.test(config.url)

    if (
      apiError.isUnauthorized &&
      config &&
      !config.__authRetry &&
      !isAuthEndpoint &&
      canRefreshSession()
    ) {
      config.__authRetry = true
      try {
        await refreshOnce()
        sessionExpiryNotified = false
        config.headers.Authorization = `Bearer ${getCurrentToken() ?? ''}`
        return httpClient.request(config)
      } catch {
        notifySessionExpiredOnce()
      }
    } else if (apiError.isUnauthorized) {
      notifySessionExpiredOnce()
    }

    // Se rechaza con el error YA normalizado. A partir de aquí, todo el
    // proyecto trabaja con `ApiError` y nunca con errores crudos de Axios.
    return Promise.reject(apiError)
  },
)

/**
 * Atajos tipados sobre el cliente.
 *
 * El genérico `<T>` es lo que devuelve el backend. Ojo: TypeScript CONFÍA en
 * lo que le digas aquí — no valida nada en tiempo de ejecución. Por eso la
 * respuesta debe pasarse siempre por un esquema de Zod en la capa `api/` de
 * cada feature. Ver docs/03-capa-de-datos.md.
 */
export const api = {
  get: async <T>(url: string, config?: Parameters<AxiosInstance['get']>[1]): Promise<T> => {
    const { data } = await httpClient.get<T>(url, config)
    return data
  },

  post: async <T>(url: string, body?: unknown): Promise<T> => {
    const { data } = await httpClient.post<T>(url, body)
    return data
  },

  put: async <T>(url: string, body?: unknown): Promise<T> => {
    const { data } = await httpClient.put<T>(url, body)
    return data
  },

  patch: async <T>(url: string, body?: unknown): Promise<T> => {
    const { data } = await httpClient.patch<T>(url, body)
    return data
  },

  delete: async <T>(url: string, body?: unknown): Promise<T> => {
    const { data } = await httpClient.delete<T>(
      url,
      body === undefined ? undefined : { data: body },
    )
    return data
  },
}
